import { useState, useEffect } from "react";
import {
	Country,
	Estate,
	LocationPoint,
} from "src/Components/Filters/LocationSearch/types";

export const LOCAL_STORAGE_KEY = "recient_locations_list";

export type RecentSearchType = "location" | "keyword" | "reference";

export type RecentSearchItem =
	| {
			type: "location";
			id: string;
			name: string;
			country: Country[];
			location_point: LocationPoint;
			estate: Estate;
			slug: string[];
	  }
	| {
			type: "keyword";
			name: string;
	  }
	| {
			type: "reference";
			id: string;
			code: string;
			title: string;
			property_type: {
				name: string;
				id: number;
			};
			link: string;
	  };

export const useRecentSearches = () => {
	const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([]);
	const savedSearches =
		typeof window !== "undefined"
			? window.localStorage.getItem(LOCAL_STORAGE_KEY)
			: null;
			
	useEffect(() => {
		const loadSearches = () => {
			if (savedSearches) {
				setRecentSearches(JSON.parse(savedSearches));
			}
		};
		loadSearches();
	}, [savedSearches]);

	const addRecentSearch = (search: RecentSearchItem) => {
		setRecentSearches(prev => {
			// Evitar duplicados
			const newSearches = prev.filter(item => {
				if (item.type !== search.type) return true;
				if (search.type === "keyword" && item.type === "keyword") return item.name !== search.name
				else if (search.type === "reference" && item.type === "reference") return item.code !== search.code
				else if (item.id && search.id) return item.id !== search.id;
				return true;
			});

			// Limitar a 5 elementos (últimas búsquedas)
			const updatedSearches = [search, ...newSearches].slice(0, 5);

			localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedSearches));
			return updatedSearches;
		});
	};

	// Filtros específicos para mantener compatibilidad
	const recentLocations = recentSearches.filter(
		(item): item is Extract<RecentSearchItem, { type: "location" }> => item.type === "location"
	);

	const recentKeywords = recentSearches.filter(
		(item): item is Extract<RecentSearchItem, { type: "keyword" }> => item.type === "keyword"
	);

	const recentReferences = recentSearches.find(
		(item): item is Extract<RecentSearchItem, { type: "reference" }> =>
			item.type === "reference"
	); // Devuelve un único objeto o undefined

	return {
		recentSearches,
		recentLocations,
		recentKeywords,
		recentReferences,
		addRecentSearch,
	};
};
